import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import { doc, onSnapshot } from 'firebase/firestore'

import { POLICY_COMPLIANCE_QUERY_KEYS } from '_services/graphql/policy-compliance.queries'
import { READINESS_DEPENDENT_QUERY_KEYS } from '_hooks/policy-compliance-query-keys.constants'
import { PENDING_ITEMS_HANDOFF_QUERY_KEYS } from '_services/graphql/pending-items-handoff.queries'
import {
  cancelPendingItemsHandoff,
  createPendingItemsHandoff,
  fetchActivePendingItemsHandoffs,
  fetchEquipmentReadiness,
  fetchEquipmentReadinessBatch,
  fetchPendingItemsHandoff,
  fetchPendingItemsRecipients,
  type EquipmentReadinessRequest,
  type EquipmentReadinessRow,
  type PendingItemsHandoffRow,
} from '_services/pending-items-handoff.service'
import { computeHandoffPollDelayMs } from '_components/form-handoff/form-handoff.utils'
import {
  computeSlowHandoffRefetchMs,
  isPendingHandoffTerminal,
  isPendingItemsSiteEvent,
  resolveHandoffRefetchMs,
} from '_components/check-in-widget/policy-gate/policy-gate.utils'

import { db } from '../config/firebase.config'

const STATUS_COLLECTION = 'pendingItemsHandoffStatus'
const HANDOFFS_SUBCOLLECTION = 'handoffs'
const CHECKIN_EVENTS_COLLECTION = 'checkinEvents'
const SITES_SUBCOLLECTION = 'sites'

export const PENDING_ITEMS_DEPENDENT_QUERY_KEYS: readonly string[] = [
  ...new Set<string>([
    ...READINESS_DEPENDENT_QUERY_KEYS,
    POLICY_COMPLIANCE_QUERY_KEYS.clientPolicyCompliance,
    POLICY_COMPLIANCE_QUERY_KEYS.clientPolicyAgreements,
    POLICY_COMPLIANCE_QUERY_KEYS.clientPolicyRelations,
    PENDING_ITEMS_HANDOFF_QUERY_KEYS.equipmentReadiness,
    PENDING_ITEMS_HANDOFF_QUERY_KEYS.recipients,
    PENDING_ITEMS_HANDOFF_QUERY_KEYS.activeHandoffs,
    PENDING_ITEMS_HANDOFF_QUERY_KEYS.handoff,
  ]),
]

const useInvalidatePendingItems = () => {
  const queryClient = useQueryClient()
  return () =>
    Promise.all(
      PENDING_ITEMS_DEPENDENT_QUERY_KEYS.map((key) => queryClient.invalidateQueries([key]))
    )
}

export const EQUIPMENT_READINESS_BADGE_STALE_MS = 30_000

export const useEquipmentReadinessBatch = (
  siteId: string | null | undefined,
  chunks: readonly EquipmentReadinessRequest[][],
  enabled: boolean
) =>
  useQueries(
    chunks.map((requests) => ({
      queryKey: [PENDING_ITEMS_HANDOFF_QUERY_KEYS.equipmentReadinessBatch, { siteId, requests }],
      queryFn: (): Promise<EquipmentReadinessRow[]> =>
        fetchEquipmentReadinessBatch({ siteId: siteId ?? '', requests }),
      enabled: enabled && Boolean(siteId),
      staleTime: EQUIPMENT_READINESS_BADGE_STALE_MS,
    }))
  )

export const useEquipmentReadiness = (
  args: {
    siteId: string | null | undefined
    clientId: string | null | undefined
    equipmentId: string | null | undefined
  },
  enabled: boolean,
  staleTime = 0
) =>
  useQuery(
    [PENDING_ITEMS_HANDOFF_QUERY_KEYS.equipmentReadiness, args],
    () =>
      fetchEquipmentReadiness({
        siteId: args.siteId ?? '',
        clientId: args.clientId ?? '',
        equipmentId: args.equipmentId ?? '',
      }),
    {
      enabled: enabled && Boolean(args.siteId && args.clientId && args.equipmentId),
      staleTime,
    }
  )

export const useActivePendingItemsHandoffs = (
  siteId: string | null | undefined,
  clientId: string | null | undefined,
  enabled: boolean
) =>
  useQuery(
    [PENDING_ITEMS_HANDOFF_QUERY_KEYS.activeHandoffs, { siteId, clientId }],
    () => fetchActivePendingItemsHandoffs({ siteId: siteId ?? '', clientId: clientId ?? '' }),
    { enabled: enabled && Boolean(siteId && clientId) }
  )

export const usePendingItemsRecipients = (
  siteId: string | null | undefined,
  clientId: string | null | undefined,
  enabled: boolean
) =>
  useQuery(
    [PENDING_ITEMS_HANDOFF_QUERY_KEYS.recipients, { siteId, clientId }],
    () => fetchPendingItemsRecipients({ siteId: siteId ?? '', clientId: clientId ?? '' }),
    { enabled: enabled && Boolean(siteId && clientId), retry: false }
  )

const buildActiveHandoffsKey = (handoff: Pick<PendingItemsHandoffRow, 'siteId' | 'clientId'>) => [
  PENDING_ITEMS_HANDOFF_QUERY_KEYS.activeHandoffs,
  { siteId: handoff.siteId, clientId: handoff.clientId },
]

export const useCreatePendingItemsHandoff = () => {
  const queryClient = useQueryClient()
  const invalidate = useInvalidatePendingItems()
  return useMutation(createPendingItemsHandoff, {
    onSuccess: (handoff) => {
      queryClient.setQueryData(
        [PENDING_ITEMS_HANDOFF_QUERY_KEYS.handoff, handoff.handoffId],
        handoff
      )
      queryClient.setQueryData<PendingItemsHandoffRow[]>(
        buildActiveHandoffsKey(handoff),
        (current = []) => [handoff, ...current.filter((row) => row.handoffId !== handoff.handoffId)]
      )
      return invalidate()
    },
  })
}

export const useCancelPendingItemsHandoff = () => {
  const queryClient = useQueryClient()
  const invalidate = useInvalidatePendingItems()
  return useMutation(cancelPendingItemsHandoff, {
    onSuccess: (handoff) => {
      queryClient.setQueryData(
        [PENDING_ITEMS_HANDOFF_QUERY_KEYS.handoff, handoff.handoffId],
        handoff
      )
      return invalidate()
    },
  })
}

const useInvalidateHandoffDependents = () => {
  const queryClient = useQueryClient()
  return () =>
    Promise.all(
      PENDING_ITEMS_DEPENDENT_QUERY_KEYS.filter(
        (key) => key !== PENDING_ITEMS_HANDOFF_QUERY_KEYS.handoff
      ).map((key) => queryClient.invalidateQueries([key]))
    )
}

// The Firestore doc only says "something changed"; every signal re-reads the handoff from the API,
// which stays the source of truth. Polling covers a listener that fails or a missing corporate, and
// a slow refetch recovers a signal that never arrives while the listener looks healthy (TDD DR4).
// Both keep running while the tab is in the background, because the desk screen showing the QR
// code is often behind another window while the client signs on their phone.
export const usePendingItemsHandoffStatus = (input: {
  handoffId: string
  corporateId: string | null | undefined
}) => {
  const queryClient = useQueryClient()
  const invalidateDependents = useInvalidateHandoffDependents()
  const [hasListenerFailed, setHasListenerFailed] = useState(false)
  const [delays] = useState(() => ({
    pollDelayMs: computeHandoffPollDelayMs(Math.random()),
    slowDelayMs: computeSlowHandoffRefetchMs(Math.random()),
  }))
  const seenVersions = useRef(new Map<string, number>())
  const isPolling = hasListenerFailed || !input.corporateId
  const queryKey = [PENDING_ITEMS_HANDOFF_QUERY_KEYS.handoff, input.handoffId]

  const query = useQuery<PendingItemsHandoffRow>(
    queryKey,
    () => fetchPendingItemsHandoff(input.handoffId),
    {
      enabled: Boolean(input.handoffId),
      staleTime: 0,
      refetchInterval: (data) =>
        resolveHandoffRefetchMs({ status: data?.status, isPolling, ...delays }),
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: 'always',
      onSuccess: (data) => {
        const seen = seenVersions.current.get(data.handoffId)
        seenVersions.current.set(data.handoffId, data.statusVersion)
        const moved =
          seen === undefined ? isPendingHandoffTerminal(data.status) : seen < data.statusVersion
        if (moved) {
          void invalidateDependents()
        }
      },
    }
  )

  useEffect(() => {
    if (!input.corporateId || !input.handoffId) {
      return undefined
    }
    const statusDoc = doc(
      db,
      STATUS_COLLECTION,
      input.corporateId,
      HANDOFFS_SUBCOLLECTION,
      input.handoffId
    )
    return onSnapshot(
      statusDoc,
      () => {
        void queryClient.invalidateQueries([
          PENDING_ITEMS_HANDOFF_QUERY_KEYS.handoff,
          input.handoffId,
        ])
      },
      () => setHasListenerFailed(true)
    )
  }, [input.corporateId, input.handoffId, queryClient])

  return {
    handoff: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}

export type PendingItemsSiteEvent = { clientId: string; status: string | null }

// The site check-in doc is also bumped for pending-items changes (kind PENDING_ITEMS, no
// checkInId), which the check-in listeners ignore; this listener reads only those bumps.
export const usePendingItemsSiteEvents = (
  input: { corporateId: string | null | undefined; siteId: string | null | undefined },
  enabled: boolean,
  onEvent: (event: PendingItemsSiteEvent) => void
) => {
  const onEventRef = useRef(onEvent)
  useEffect(() => {
    onEventRef.current = onEvent
  }, [onEvent])

  useEffect(() => {
    if (!enabled || !input.corporateId || !input.siteId) {
      return undefined
    }
    let isPrimed = false
    return onSnapshot(
      doc(db, CHECKIN_EVENTS_COLLECTION, input.corporateId, SITES_SUBCOLLECTION, input.siteId),
      (snapshot) => {
        if (!isPrimed) {
          isPrimed = true
          return
        }
        const data = snapshot.exists() ? snapshot.data() : null
        if (isPendingItemsSiteEvent(data)) {
          onEventRef.current({ clientId: data.clientId, status: data.status ?? null })
        }
      },
      () => undefined
    )
  }, [enabled, input.corporateId, input.siteId])
}

export const usePendingItemsComplianceRefresh = (input: {
  corporateId: string | null | undefined
  siteId: string | null | undefined
  clientId: string | null | undefined
  enabled: boolean
}) => {
  const invalidate = useInvalidatePendingItems()
  usePendingItemsSiteEvents(input, input.enabled, (event) => {
    if (event.clientId === input.clientId) {
      void invalidate()
    }
  })
}
